import React, { useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * iter-121.154 — Guided-setup return banner.
 * iter-121.189 — Make it PERSISTENT across the whole onboarding session.
 *
 * Rendered once inside AppShell. Originally it only showed while the current
 * URL carried a `?returnTo=/onboarding…` param, which the first-run wizard
 * emits when it deep-links a user to a real page. Problem: navigations that
 * drop the param (e.g. clicking "Use" on a prebuilt template → /dashboards/:id)
 * made the strip vanish, so it was inconsistent.
 *
 * Fix: once the wizard hands off with `?returnTo=/onboarding…`, we remember it
 * in `sessionStorage` (tab-scoped, auto-clears when the tab closes). The strip
 * then stays visible on every page for the rest of the guided-setup session —
 * regardless of how the user navigates — until onboarding is finished/skipped
 * (Onboarding.jsx clears the marker). Normal logins never set the marker, so
 * the strip stays invisible outside of first-run onboarding.
 */
export const ONBOARDING_RETURN_KEY = "analyzio_onboarding_return";

export function clearOnboardingReturn() {
    try { sessionStorage.removeItem(ONBOARDING_RETURN_KEY); } catch { /* ignore */ }
}

export default function OnboardingReturnBanner() {
    const [sp] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const urlReturnTo = sp.get("returnTo");

    // Persist the marker as soon as the wizard hands off with the param.
    useEffect(() => {
        if (urlReturnTo && urlReturnTo.startsWith("/onboarding")) {
            try { sessionStorage.setItem(ONBOARDING_RETURN_KEY, urlReturnTo); } catch { /* ignore */ }
        }
    }, [urlReturnTo]);

    // Effective target: live URL param wins, else the remembered session value.
    let stored = null;
    try { stored = sessionStorage.getItem(ONBOARDING_RETURN_KEY); } catch { /* ignore */ }
    const returnTo = (urlReturnTo && urlReturnTo.startsWith("/onboarding"))
        ? urlReturnTo
        : (stored && stored.startsWith("/onboarding") ? stored : null);

    // Never render the strip while the user is actually on the onboarding page
    // itself (would be redundant with the wizard chrome).
    if (!returnTo || location.pathname.startsWith("/onboarding")) return null;

    return (
        <div
            className="w-full bg-primary/10 border-b border-primary/30 px-4 py-2 flex items-center justify-between gap-3"
            data-testid="onboarding-return-banner"
        >
            <span className="text-[12px] sm:text-sm text-foreground font-medium flex items-center gap-2 min-w-0">
                <span className="inline-flex h-5 items-center rounded-sm bg-primary/20 px-1.5 text-[10px] font-mono uppercase tracking-wider text-primary shrink-0">
                    Setup
                </span>
                <span className="truncate">You're exploring as part of guided setup.</span>
            </span>
            <button
                type="button"
                onClick={() => navigate(returnTo)}
                className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-sm bg-primary text-primary-foreground text-[12px] font-semibold hover:bg-primary/90 transition-colors"
                data-testid="onboarding-return-btn"
            >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to setup
            </button>
        </div>
    );
}
